import {
  ActiveGameInfo,
  abandonActiveGame,
  claimDailyMatchPoints,
  ensureUser,
  fetchActiveGame,
  fetchMatchPointsInfo,
  fetchMatchPointsInfoWithToken,
  fetchMeWithToken,
  getLoginToken,
  guobiaoNewbieStart,
  MatchPointsInfo,
  newbieStart,
  setLoginToken,
  UserPublic,
} from './user';
import { Client } from './client';
import { createFirefliesFx, type FirefliesFxHandle } from './fireflies-fx';
import { setupDesignCanvasScale } from './design-canvas';
import { ensurePwaManifestLink } from './pwa-manifest';
import { guobiaoConfigForSeat } from './guobiao-preferences';
import { getBasePath, getWsUrl } from './ws-url';

ensurePwaManifestLink();

const statusEl = document.getElementById('landing-status') as HTMLElement;
const btnAccount = document.getElementById('btn-account') as HTMLButtonElement;
const btnStartGame = document.getElementById('btn-start-game') as HTMLButtonElement | null;
const btnFriendRoom = document.getElementById('btn-friend-room') as HTMLAnchorElement | null;
const btnCoach = document.getElementById('btn-coach') as HTMLAnchorElement | null;

const landingRoot = document.getElementById('landing-root') as HTMLElement | null;
const landingCanvasWrap = document.getElementById('landing-canvas-wrap') as HTMLElement | null;
const landingCanvas = document.getElementById('landing-canvas') as HTMLElement | null;
const landingBgFx = document.getElementById('landing-bg-fx') as HTMLElement | null;

const modal = document.getElementById('modal') as HTMLElement;
const modalTitle = document.getElementById('modal-title') as HTMLElement;
const modalText = document.getElementById('modal-text') as HTMLElement;
const modalBody = document.getElementById('modal-body') as HTMLElement;
const modalClose = document.getElementById('modal-close') as HTMLButtonElement;

let currentUser: UserPublic | null = null;
let currentMatchPoints: MatchPointsInfo | null = null;
let pendingDailyClaim: MatchPointsInfo | null = null;
let insufficientRedirectTimeoutId: number | null = null;
let insufficientRedirectIntervalId: number | null = null;
const DEFAULT_READY_STATUS = '准备就绪：点击开始游戏选择玩法，查看 AI 荐牌、教学与复盘。';
const MULTI_TABLE_DENIED_KEYWORD = '你已在其他对局中';
type FriendRoomWaitMode = 'noTimeout' | 'timeoutAuto';
type LandingGameType = 'BLOOD_BATTLE' | 'GUOBIAO';
const GAME_TYPE_PREF_KEY = 'mjlab.landing.preferredGameType';
type FriendRoomCreateResult = {
  gameId: string;
  gameType: LandingGameType;
  gameTypeLabel: string;
  sharePath: string;
  fullUrl: string;
  waitModeLabel: string;
  waitModeApplied: boolean;
  waitModeWarning: string | null;
};

let friendRoomCreateClient: Client | null = null;

function setupLandingCanvasScale(): void {
  if (!landingRoot || !landingCanvasWrap || !landingCanvas) return;

  let latestScale = 1;
  let firefliesFx: FirefliesFxHandle | null = null;
  let firefliesStageHeight = 0;

  const canvasScale = setupDesignCanvasScale({
    root: landingRoot,
    wrap: landingCanvasWrap,
    canvas: landingCanvas,
    designWidth: 720,
    maxWidth: 1440,
    onAfterApply: ({ scale, baseHeight, designWidth }) => {
      latestScale = scale;
      if (landingBgFx && baseHeight > 0 && baseHeight !== firefliesStageHeight) {
        firefliesFx?.destroy();
        firefliesStageHeight = baseHeight;
        firefliesFx = createFirefliesFx(landingBgFx, {
          stageWidth: designWidth,
          stageHeight: baseHeight,
          getStageScale: () => latestScale,
          maxPixelRatio: 1.8,
        });
      }
    },
  });

  window.addEventListener(
    'beforeunload',
    () => {
      canvasScale?.destroy();
      firefliesFx?.destroy();
      firefliesFx = null;
    },
    { once: true },
  );
}

function formatPoints(v: number): string {
  if (!Number.isFinite(v)) return '0';
  return new Intl.NumberFormat('zh-CN').format(Math.max(0, Math.trunc(v)));
}

function normalizeLandingGameType(value: unknown): LandingGameType {
  return value === 'GUOBIAO' ? 'GUOBIAO' : 'BLOOD_BATTLE';
}

function readPreferredGameType(): LandingGameType {
  try {
    return normalizeLandingGameType(window.localStorage.getItem(GAME_TYPE_PREF_KEY));
  } catch {
    return 'BLOOD_BATTLE';
  }
}

function writePreferredGameType(gameType: LandingGameType): void {
  try {
    window.localStorage.setItem(GAME_TYPE_PREF_KEY, gameType);
  } catch {
    // ignore
  }
}

function gameTypeLabel(gameType: LandingGameType): string {
  return gameType === 'GUOBIAO' ? '国际标准' : '血战到底';
}

function newbieModeLabel(gameType: LandingGameType): string {
  return `${gameTypeLabel(gameType)}新手房`;
}

function friendModeLabel(gameType: LandingGameType): string {
  return `${gameTypeLabel(gameType)}好友房`;
}

function styleChoiceButton(btn: HTMLButtonElement): void {
  btn.style.minHeight = '44px';
  btn.style.flex = '1 1 0';
}

function renderWelcomeStatus(prefix?: string): void {
  if (prefix) {
    statusEl.textContent = prefix;
    return;
  }
  if (!currentUser) {
    statusEl.textContent = DEFAULT_READY_STATUS;
    return;
  }
  const points = currentMatchPoints?.matchPoints;
  if (typeof points === 'number') {
    statusEl.textContent = `欢迎，${currentUser.nickname} · 对局积分：${formatPoints(points)}`;
    return;
  }
  statusEl.textContent = `欢迎，${currentUser.nickname}`;
}

function openModal(title: string, text: string, body: HTMLElement): void {
  modalTitle.textContent = title;
  modalText.textContent = text;
  modalBody.innerHTML = '';
  modalBody.appendChild(body);
  modal.classList.add('open');
}

function closeModal(): void {
  cancelFriendRoomCreateClient();
  modal.classList.remove('open');
  if (pendingDailyClaim) {
    const info = pendingDailyClaim;
    pendingDailyClaim = null;
    window.setTimeout(() => {
      openDailyClaimModal(info);
    }, 0);
  }
}

modalClose.onclick = closeModal;
modal.onclick = (e) => {
  if (e.target === modal) closeModal();
};

setupLandingCanvasScale();

btnAccount.onclick = () => {
  openModal('账号 / 口令', '备份当前口令，或切换到另一个账号。', buildAccountBody());
};

if (btnStartGame) {
  btnStartGame.onclick = () => void onStartGame();
}

if (btnFriendRoom) {
  btnFriendRoom.onclick = (ev) => {
    ev.preventDefault();
    openFriendRoomQuickModal();
  };
}

if (btnCoach) {
  btnCoach.onclick = (ev) => {
    ev.preventDefault();
    void onStartModeLink(btnCoach, '教练模式');
  };
}

function buildLoginBody(): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'field';

  const input = document.createElement('input');
  input.placeholder = '粘贴登录口令（token）';
  input.setAttribute('autocapitalize', 'off');
  input.autocomplete = 'off';
  input.spellcheck = false;

  const row = document.createElement('div');
  row.className = 'row';

  const btn = document.createElement('button');
  btn.className = 'btn-mini primary';
  btn.type = 'button';
  btn.textContent = '登录';

  const msg = document.createElement('div');
  msg.className = 'p';

  btn.onclick = async () => {
    msg.textContent = '';
    const token = input.value.trim();
    if (!token) {
      msg.textContent = '请输入登录口令。';
      return;
    }
    try {
      const { user } = await fetchMeWithToken(token);
      const points = await fetchMatchPointsInfoWithToken(token);
      setLoginToken(token);
      currentUser = user;
      currentMatchPoints = points;
      renderWelcomeStatus();
      if (currentMatchPoints.canClaimDaily) {
        queueDailyClaimModal(currentMatchPoints);
      }
      closeModal();
    } catch (err: unknown) {
      msg.textContent = `登录失败：${String((err as any)?.message ?? err)}`;
    }
  };

  row.appendChild(btn);
  wrap.appendChild(input);
  wrap.appendChild(row);
  wrap.appendChild(msg);
  return wrap;
}

function buildTokenBody(loginToken: string, apiToken: string | null): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'field';

  wrap.appendChild(buildCopyField('登录口令（请妥善保存）', loginToken));
  if (apiToken) {
    wrap.appendChild(buildCopyField('API Token（仅展示一次）', apiToken));
  }

  const hint = document.createElement('div');
  hint.className = 'p';
  hint.textContent = apiToken
    ? '提示：API Token 只在创建/重置时展示一次，丢失只能重置。'
    : '提示：如果你更换设备，需要用登录口令登录。';
  wrap.appendChild(hint);

  return wrap;
}

function buildCopyField(label: string, value: string): HTMLElement {
  const outer = document.createElement('div');

  const t = document.createElement('div');
  t.className = 'p';
  t.textContent = label;

  const input = document.createElement('input');
  input.value = value;
  input.readOnly = true;

  const row = document.createElement('div');
  row.className = 'row';

  const copy = document.createElement('button');
  copy.className = 'btn-mini ghost';
  copy.type = 'button';
  copy.textContent = '复制';
  copy.onclick = async () => {
    await copyText(value);
    copy.textContent = '已复制';
    setTimeout(() => (copy.textContent = '复制'), 900);
  };

  row.appendChild(copy);
  outer.appendChild(t);
  outer.appendChild(input);
  outer.appendChild(row);
  return outer;
}

async function copyText(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    return;
  } catch {
    // fallback
  }
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.style.position = 'fixed';
  ta.style.left = '-9999px';
  document.body.appendChild(ta);
  ta.select();
  try {
    document.execCommand('copy');
  } finally {
    document.body.removeChild(ta);
  }
}

function friendWaitModeLabel(mode: FriendRoomWaitMode): string {
  return mode === 'timeoutAuto' ? '只超时自动（6 分钟）' : '玩家点击过（不限时）';
}

function cancelFriendRoomCreateClient(): void {
  if (!friendRoomCreateClient) return;
  try {
    friendRoomCreateClient.disconnect();
  } catch {
    // ignore
  } finally {
    friendRoomCreateClient = null;
  }
}

async function shareRoomLink(url: string, gameType: LandingGameType): Promise<'shared' | 'copied' | 'cancelled' | 'failed'> {
  const shareFn = (navigator as any)?.share;
  if (typeof shareFn === 'function') {
    try {
      await shareFn.call(navigator, {
        title: 'MJLab 好友房',
        text: `来一起玩${gameTypeLabel(gameType)}`,
        url,
      });
      return 'shared';
    } catch (err: unknown) {
      if (String((err as any)?.name ?? '') === 'AbortError') {
        return 'cancelled';
      }
    }
  }
  try {
    await copyText(url);
    return 'copied';
  } catch {
    return 'failed';
  }
}

async function createFriendRoom(waitMode: FriendRoomWaitMode, gameType: LandingGameType): Promise<FriendRoomCreateResult> {
  cancelFriendRoomCreateClient();
  return await new Promise((resolve, reject) => {
    const client = new Client();
    friendRoomCreateClient = client;

	    let settled = false;
	    let connected = false;
	    let authoritative = false;
	    let gameId = '';
	    let seatConfirmed = false;
	    let timeoutId: number | null = null;

    const clearTimers = (): void => {
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    const cleanup = (): void => {
      clearTimers();
      if (friendRoomCreateClient === client) {
        friendRoomCreateClient = null;
      }
      try {
        client.disconnect();
      } catch {
        // ignore
      }
    };

    const fail = (err: unknown): void => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(err instanceof Error ? err : new Error(String(err)));
    };

	    const maybeDone = (): void => {
	      if (settled) return;
	      if (!connected || !gameId || !seatConfirmed) return;
	      settled = true;
	      cleanup();
	      const variant = gameType === 'GUOBIAO' ? '&variant=guobiao' : '';
	      const sharePath = `${getBasePath()}hand/?gameId=${encodeURIComponent(gameId)}${variant}`;
	      const fullUrl = new URL(sharePath, window.location.origin).toString();
	      const waitModeApplied = authoritative;
	      const waitModeWarning = waitModeApplied
	        ? null
	        : '当前环境未开启服务器权威模式（MJLAB_AUTHORITATIVE=1），等待模式配置不会生效（不会自动超时托管）。';
	      resolve({
	        gameId,
	        gameType,
	        gameTypeLabel: gameTypeLabel(gameType),
	        sharePath,
	        fullUrl,
	        waitModeLabel: friendWaitModeLabel(waitMode),
	        waitModeApplied,
	        waitModeWarning,
	      });
	    };

    timeoutId = window.setTimeout(() => {
      fail(new Error('创建房间超时，请检查网络后重试。'));
    }, 9000);

	    client.on('connect', (game) => {
	      connected = true;
	      authoritative = !!(game as any)?.authoritative;
	      gameId = game.gameId;
	      client.seats.set(client.playerId(), { seat: 0 } as any);
	      maybeDone();
	    });

    client.seats.on('update', () => {
      if (settled) return;
      if (client.seat === 0) {
        seatConfirmed = true;
      }
      maybeDone();
    });

    client.on('serverError', (error) => {
      const msg = String(error ?? '').trim();
      fail(new Error(msg || '创建房间失败'));
    });

    client.on('disconnect', () => {
      if (settled) return;
      fail(new Error(connected ? '创建房间时连接中断，请重试。' : '连接服务器失败，请稍后再试。'));
    });

    client.new(getWsUrl(), {
      gameType,
      friendConfig: {
        waitMode,
        timeoutMs: waitMode === 'timeoutAuto' ? 6 * 60 * 1000 : null,
      },
      guobiaoConfig: gameType === 'GUOBIAO' ? guobiaoConfigForSeat(0) : undefined,
    });
  });
}

function buildFriendRoomQuickBody(): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'field';
  let selectedGameType = readPreferredGameType();
  let selectedWaitMode: FriendRoomWaitMode = 'timeoutAuto';
  const gameTypeButtons: HTMLButtonElement[] = [];
  const waitModeButtons: HTMLButtonElement[] = [];

  const intro = document.createElement('div');
  intro.className = 'p';
  wrap.appendChild(intro);

  const mobileHint = document.createElement('div');
  mobileHint.className = 'p';
  mobileHint.textContent = '如需房主控制台（发牌/重置分数等），可继续使用 /mobile/ 页面。';
  wrap.appendChild(mobileHint);

  const gameTypeTitle = document.createElement('div');
  gameTypeTitle.className = 'p';
  gameTypeTitle.textContent = '玩法';
  wrap.appendChild(gameTypeTitle);

  const gameTypeRow = document.createElement('div');
  gameTypeRow.className = 'row';
  for (const gameType of ['BLOOD_BATTLE', 'GUOBIAO'] as const) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = gameTypeLabel(gameType);
    styleChoiceButton(btn);
    btn.onclick = () => {
      if (btn.disabled) return;
      selectedGameType = gameType;
      writePreferredGameType(gameType);
      syncGameTypeUi();
    };
    gameTypeButtons.push(btn);
    gameTypeRow.appendChild(btn);
  }
  wrap.appendChild(gameTypeRow);

  const waitTitle = document.createElement('div');
  waitTitle.className = 'p';
  waitTitle.textContent = '等待模式';
  wrap.appendChild(waitTitle);

  const waitModeRow = document.createElement('div');
  waitModeRow.className = 'row';
  for (const waitMode of ['timeoutAuto', 'noTimeout'] as const) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = waitMode === 'timeoutAuto' ? '只超时自动' : '不托管';
    styleChoiceButton(btn);
    btn.onclick = () => {
      if (btn.disabled) return;
      selectedWaitMode = waitMode;
      syncGameTypeUi();
    };
    waitModeButtons.push(btn);
    waitModeRow.appendChild(btn);
  }
  wrap.appendChild(waitModeRow);

  const row = document.createElement('div');
  row.className = 'row';
  const createBtn = document.createElement('button');
  createBtn.className = 'btn-mini primary';
  createBtn.type = 'button';
  row.appendChild(createBtn);
  wrap.appendChild(row);

  const status = document.createElement('div');
  status.className = 'p';
  status.textContent = '';
  wrap.appendChild(status);

  const resultWrap = document.createElement('div');
  resultWrap.className = 'field';
  resultWrap.style.display = 'none';

  const linkTitle = document.createElement('div');
  linkTitle.className = 'p';
  linkTitle.textContent = '房间链接（分享给好友）';
  resultWrap.appendChild(linkTitle);

  const linkInput = document.createElement('input');
  linkInput.readOnly = true;
  resultWrap.appendChild(linkInput);

  const actionRow = document.createElement('div');
  actionRow.className = 'row';

  const shareBtn = document.createElement('button');
  shareBtn.className = 'btn-mini primary';
  shareBtn.type = 'button';
  shareBtn.textContent = '分享';
  actionRow.appendChild(shareBtn);

  const copyBtn = document.createElement('button');
  copyBtn.className = 'btn-mini ghost';
  copyBtn.type = 'button';
  copyBtn.textContent = '复制链接';
  actionRow.appendChild(copyBtn);

  const enterBtn = document.createElement('button');
  enterBtn.className = 'btn-mini primary';
  enterBtn.type = 'button';
  enterBtn.textContent = '进入对局';
  actionRow.appendChild(enterBtn);

  const enterNewTabBtn = document.createElement('button');
  enterNewTabBtn.className = 'btn-mini ghost';
  enterNewTabBtn.type = 'button';
  enterNewTabBtn.textContent = '新标签进入';
  actionRow.appendChild(enterNewTabBtn);

  resultWrap.appendChild(actionRow);
  wrap.appendChild(resultWrap);

  let creating = false;
  let currentResult: FriendRoomCreateResult | null = null;

  function syncGameTypeUi(): void {
    intro.textContent =
      selectedGameType === 'BLOOD_BATTLE'
        ? '血战到底好友房按所选等待模式执行。创建后会生成分享链接，并可直接进入好友房对局。'
        : '国际标准好友房按国标规则和所选等待模式开局。创建后会生成分享链接，并可直接进入好友房对局。';
    gameTypeButtons.forEach((btn, index) => {
      const gameType = index === 0 ? 'BLOOD_BATTLE' : 'GUOBIAO';
      btn.className = selectedGameType === gameType ? 'btn-mini primary' : 'btn-mini ghost';
    });
    waitModeButtons.forEach((btn, index) => {
      const waitMode = index === 0 ? 'timeoutAuto' : 'noTimeout';
      btn.className = selectedWaitMode === waitMode ? 'btn-mini primary' : 'btn-mini ghost';
    });
    createBtn.textContent = `创建${gameTypeLabel(selectedGameType)}房间`;
  }

  const setCreating = (on: boolean): void => {
    creating = on;
    createBtn.disabled = on;
    waitModeButtons.forEach((btn) => {
      btn.disabled = on;
    });
    gameTypeButtons.forEach((btn) => {
      btn.disabled = on;
    });
  };

  const reopenSelfAndRetry = async (): Promise<void> => {
    openModal('创建好友房', '创建后可直接分享链接并进入对局。', wrap);
    setCreating(false);
    await runCreateFlow();
  };

  const runCreateFlow = async (): Promise<void> => {
    if (creating) return;
    setCreating(true);
    const gameType = selectedGameType;
    const modeLabel = friendModeLabel(gameType);
    writePreferredGameType(gameType);
    currentResult = null;
    resultWrap.style.display = 'none';
    status.textContent = '正在校验账号与对局积分…';
    try {
      const allowed = await ensureStartAllowed(modeLabel);
      if (!allowed) {
        if (status.textContent === '正在校验账号与对局积分…') {
          status.textContent = '校验未通过，请按提示处理后重试。';
        }
        return;
      }
      const noActive = await ensureNoActiveGame(modeLabel, async () => {
        await reopenSelfAndRetry();
      });
      if (!noActive) return;

      const waitMode = selectedWaitMode;
      status.textContent = '正在创建房间…';
      const room = await createFriendRoom(waitMode, gameType);
      currentResult = room;
      linkInput.value = room.fullUrl;
      resultWrap.style.display = '';
      if (room.waitModeApplied) {
        status.textContent = room.waitModeWarning
          ? `已创建${room.gameTypeLabel}好友房。等待模式已生效：${room.waitModeLabel}。${room.waitModeWarning}`
          : `已创建${room.gameTypeLabel}好友房。等待模式已生效：${room.waitModeLabel}。请分享链接给好友。`;
      } else {
        status.textContent = room.waitModeWarning
          ? `已创建房间。等待模式未生效。${room.waitModeWarning}`
          : '已创建房间。等待模式未生效，将使用服务器默认规则。请分享链接给好友。';
      }
    } catch (err: unknown) {
      if (await handleInsufficientFromServer(modeLabel, err)) {
        return;
      }
      if (await handleMultiTableFromServer(modeLabel, err, async () => {
        await reopenSelfAndRetry();
      })) {
        return;
      }
      status.textContent = `创建失败：${String((err as any)?.message ?? err)}`;
    } finally {
      setCreating(false);
    }
  };

  createBtn.onclick = () => {
    void runCreateFlow();
  };

  shareBtn.onclick = async () => {
    if (!currentResult) return;
    const out = await shareRoomLink(currentResult.fullUrl, currentResult.gameType);
    if (out === 'shared') {
      status.textContent = '已打开系统分享面板。';
      return;
    }
    if (out === 'copied') {
      status.textContent = '当前环境不支持系统分享，已复制链接。';
      return;
    }
    if (out === 'cancelled') {
      status.textContent = '已取消分享。';
      return;
    }
    status.textContent = '分享失败，请改用复制链接。';
  };

  copyBtn.onclick = async () => {
    if (!currentResult) return;
    await copyText(currentResult.fullUrl);
    status.textContent = '已复制链接。';
  };

  enterBtn.onclick = () => {
    if (!currentResult) return;
    window.location.href = currentResult.sharePath;
  };

  enterNewTabBtn.onclick = () => {
    if (!currentResult) return;
    const popup = window.open(currentResult.sharePath, '_blank', 'noopener,noreferrer');
    if (!popup) {
      status.textContent = '新标签打开失败，请检查浏览器拦截设置。';
      return;
    }
    status.textContent = '已在新标签打开对局。';
  };

  syncGameTypeUi();
  return wrap;
}

function openFriendRoomQuickModal(): void {
  openModal('创建好友房', '创建后可直接分享链接并进入对局。', buildFriendRoomQuickBody());
}

function buildAccountBody(): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'field';

  const loginToken = getLoginToken();
  if (loginToken) {
    const title = document.createElement('div');
    title.className = 'p';
    title.textContent = currentUser ? `当前账号：${currentUser.nickname}` : '当前账号';
    wrap.appendChild(title);
    wrap.appendChild(buildCopyField('登录口令（可备份复制）', loginToken));
  } else {
    const title = document.createElement('div');
    title.className = 'p';
    title.textContent = '当前设备未登录。可输入登录口令，或创建新账号。';
    wrap.appendChild(title);
  }

  const sep = document.createElement('div');
  sep.style.height = '1px';
  sep.style.background = 'rgba(255,255,255,0.10)';
  sep.style.margin = '10px 0';
  wrap.appendChild(sep);

  const switchTitle = document.createElement('div');
  switchTitle.className = 'p';
  switchTitle.textContent = loginToken ? '切换账号：输入另一个登录口令' : '登录账号：输入登录口令';
  wrap.appendChild(switchTitle);

  const input = document.createElement('input');
  input.placeholder = '粘贴登录口令（token）';
  input.setAttribute('autocapitalize', 'off');
  input.autocomplete = 'off';
  input.spellcheck = false;

  const row = document.createElement('div');
  row.className = 'row';

  const btn = document.createElement('button');
  btn.className = 'btn-mini primary';
  btn.type = 'button';
  btn.textContent = loginToken ? '切换' : '登录';

  const msg = document.createElement('div');
  msg.className = 'p';

  btn.onclick = async () => {
    msg.textContent = '';
    const token = input.value.trim();
    if (!token) {
      msg.textContent = '请输入登录口令。';
      return;
    }
    btn.disabled = true;
    try {
      const { user } = await fetchMeWithToken(token);
      const points = await fetchMatchPointsInfoWithToken(token);
      setLoginToken(token);
      currentUser = user;
      currentMatchPoints = points;
      renderWelcomeStatus();
      if (currentMatchPoints.canClaimDaily) {
        queueDailyClaimModal(currentMatchPoints);
      }
      closeModal();
    } catch (err: unknown) {
      msg.textContent = `切换失败：${String((err as any)?.message ?? err)}`;
    } finally {
      btn.disabled = false;
    }
  };

  row.appendChild(btn);
  if (!loginToken) {
    const createBtn = document.createElement('button');
    createBtn.className = 'btn-mini ghost';
    createBtn.type = 'button';
    createBtn.textContent = '创建新账号';
    createBtn.onclick = async () => {
      msg.textContent = '正在创建账号…';
      createBtn.disabled = true;
      btn.disabled = true;
      try {
        const { created, loginToken: newLoginToken, apiToken, user } = await ensureUser();
        currentUser = user;
        currentMatchPoints = await fetchMatchPointsInfo();
        renderWelcomeStatus();
        if (created) {
          openModal('已为你创建账号', '请务必复制并保存你的登录口令。', buildTokenBody(newLoginToken, apiToken));
        } else {
          openModal('账号 / 口令', '备份当前口令，或切换到另一个账号。', buildAccountBody());
        }
        if (currentMatchPoints.canClaimDaily) {
          queueDailyClaimModal(currentMatchPoints);
        }
      } catch (err: unknown) {
        msg.textContent = `创建失败：${String((err as any)?.message ?? err)}`;
      } finally {
        createBtn.disabled = false;
        btn.disabled = false;
      }
    };
    row.appendChild(createBtn);
  }
  wrap.appendChild(input);
  wrap.appendChild(row);
  wrap.appendChild(msg);

  return wrap;
}

function buildDailyClaimBody(info: MatchPointsInfo): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'field';

  const points = document.createElement('div');
  points.className = 'p';
  points.textContent = `当前对局积分：${formatPoints(info.matchPoints)}`;
  wrap.appendChild(points);

  const hint = document.createElement('div');
  hint.className = 'p';
  hint.textContent = `今日可领取：${formatPoints(info.dailyClaimAmount)} 对局积分（每日 1 次）`;
  wrap.appendChild(hint);

  const row = document.createElement('div');
  row.className = 'row';
  const claimBtn = document.createElement('button');
  claimBtn.className = 'btn-mini primary';
  claimBtn.type = 'button';
  claimBtn.textContent = '领取';
  const msg = document.createElement('div');
  msg.className = 'p';

  claimBtn.onclick = async () => {
    claimBtn.disabled = true;
    msg.textContent = '领取中…';
    try {
      const out = await claimDailyMatchPoints();
      currentMatchPoints = {
        matchPoints: out.matchPoints,
        canClaimDaily: out.canClaimDaily,
        dailyClaimAmount: out.dailyClaimAmount,
        todayKey: out.todayKey,
        lastClaimDay: out.lastClaimDay,
      };
      renderWelcomeStatus(`领取成功，当前对局积分：${formatPoints(out.matchPoints)}`);
      closeModal();
    } catch (err: unknown) {
      const text = String((err as any)?.message ?? err);
      msg.textContent = text.includes('今日已领取') ? '今日已领取' : `领取失败：${text}`;
      if (text.includes('今日已领取') && currentMatchPoints) {
        currentMatchPoints = { ...currentMatchPoints, canClaimDaily: false };
      }
    } finally {
      claimBtn.disabled = false;
    }
  };

  row.appendChild(claimBtn);
  wrap.appendChild(row);
  wrap.appendChild(msg);
  return wrap;
}

function openDailyClaimModal(info: MatchPointsInfo): void {
  if (!info.canClaimDaily) return;
  openModal('每日对局积分', '点击领取今日免费积分。', buildDailyClaimBody(info));
}

function queueDailyClaimModal(info: MatchPointsInfo): void {
  if (!info.canClaimDaily) return;
  if (modal.classList.contains('open')) {
    pendingDailyClaim = info;
    return;
  }
  openDailyClaimModal(info);
}

function clearInsufficientRedirectTimers(): void {
  if (insufficientRedirectTimeoutId !== null) {
    window.clearTimeout(insufficientRedirectTimeoutId);
    insufficientRedirectTimeoutId = null;
  }
  if (insufficientRedirectIntervalId !== null) {
    window.clearInterval(insufficientRedirectIntervalId);
    insufficientRedirectIntervalId = null;
  }
}

function getPersonalCenterUrl(): string {
  return `${getBasePath()}me/`;
}

function buildInsufficientText(info: Pick<MatchPointsInfo, 'canClaimDaily' | 'dailyClaimAmount'>): string {
  if (info.canClaimDaily) {
    const amount = Math.max(0, Math.trunc(info.dailyClaimAmount ?? 4000));
    return `对局积分不足。今日可领取 ${amount} 对局积分，3 秒后将跳转个人中心。`;
  }
  return '对局积分不足。今日领取次数已用完，3 秒后将跳转个人中心。';
}

function fallbackMatchPointsInfo(): MatchPointsInfo {
  return {
    matchPoints: 0,
    canClaimDaily: false,
    dailyClaimAmount: 4000,
    todayKey: '',
    lastClaimDay: null,
  };
}

function openInsufficientRedirectModal(info: MatchPointsInfo, modeLabel: string): void {
  clearInsufficientRedirectTimers();
  pendingDailyClaim = null;

  let remain = 3;
  const text = buildInsufficientText(info);
  const body = document.createElement('div');
  body.className = 'field';

  const modeLine = document.createElement('div');
  modeLine.className = 'p';
  modeLine.textContent = `当前无法开始${modeLabel}。`;
  body.appendChild(modeLine);

  const countdown = document.createElement('div');
  countdown.className = 'p';
  const renderCountdown = (): void => {
    countdown.textContent = `${remain} 秒后自动跳转到个人中心。`;
  };
  renderCountdown();
  body.appendChild(countdown);

  const row = document.createElement('div');
  row.className = 'row';
  const goNowBtn = document.createElement('button');
  goNowBtn.className = 'btn-mini primary';
  goNowBtn.type = 'button';
  goNowBtn.textContent = '立即前往个人中心';
  goNowBtn.onclick = () => {
    clearInsufficientRedirectTimers();
    window.location.href = getPersonalCenterUrl();
  };
  row.appendChild(goNowBtn);
  body.appendChild(row);

  openModal('对局积分不足', text, body);

  insufficientRedirectTimeoutId = window.setTimeout(() => {
    clearInsufficientRedirectTimers();
    window.location.href = getPersonalCenterUrl();
  }, 3000);
  insufficientRedirectIntervalId = window.setInterval(() => {
    remain -= 1;
    if (remain <= 0) {
      clearInsufficientRedirectTimers();
      return;
    }
    renderCountdown();
  }, 1000);
}

async function handleInsufficientFromServer(modeLabel: string, err: unknown): Promise<boolean> {
  const msg = String((err as any)?.message ?? err);
  if (!msg.includes('对局积分不足')) return false;
  let info = currentMatchPoints ?? fallbackMatchPointsInfo();
  try {
    info = await fetchMatchPointsInfo();
    currentMatchPoints = info;
  } catch {
    // fallback to cached info
  }
  const text = buildInsufficientText(info);
  renderWelcomeStatus(text);
  openInsufficientRedirectModal(info, modeLabel);
  return true;
}

function modeLabelOfActiveGame(active: ActiveGameInfo): string {
  if (active.roomType === 'newbie') return active.gameType === 'GUOBIAO' ? '国际标准新手房' : '血战到底新手房';
  if (active.roomType === 'friend') return active.gameType === 'GUOBIAO' ? '国际标准好友房' : '血战到底好友房';
  if (active.roomType === 'practice-branch') return '练习分支';
  return '好友房';
}

function activeGameUrl(active: ActiveGameInfo): string {
  const basePath = getBasePath();
  const room = active.roomType === 'newbie' ? '&room=newbie' : '';
  const variant = active.gameType === 'GUOBIAO' ? '&variant=guobiao' : '';
  return `${basePath}hand/?gameId=${encodeURIComponent(active.gameId)}${room}${variant}`;
}

function normalizeActiveGame(raw: any): ActiveGameInfo | null {
  const gameId = typeof raw?.gameId === 'string' ? raw.gameId.trim() : '';
  if (!gameId) return null;
  const seatRaw = Number(raw?.seat);
  const seat = Number.isFinite(seatRaw) ? Math.trunc(seatRaw) : -1;
  if (seat < 0 || seat > 3) return null;
  const roomType = typeof raw?.roomType === 'string' ? raw.roomType.trim() || null : null;
  const gameType = typeof raw?.gameType === 'string' ? raw.gameType.trim() || null : null;
  return { gameId, seat, roomType, gameType };
}

function openActiveGameModal(
  active: ActiveGameInfo,
  requestModeLabel: string,
  onAbandonAndContinue?: () => void | Promise<void>
): void {
  const body = document.createElement('div');
  body.className = 'field';

  const currentLine = document.createElement('div');
  currentLine.className = 'p';
  currentLine.textContent = `你当前正在进行：${modeLabelOfActiveGame(active)}（座位 ${active.seat + 1}）。`;
  body.appendChild(currentLine);

  const requestLine = document.createElement('div');
  requestLine.className = 'p';
  requestLine.textContent = `目标：开始${requestModeLabel}。为避免多桌并发，请先回到旧牌局。`;
  body.appendChild(requestLine);

  const row = document.createElement('div');
  row.className = 'row';

  const continueBtn = document.createElement('button');
  continueBtn.className = 'btn-mini primary';
  continueBtn.type = 'button';
  continueBtn.textContent = '继续旧牌局（推荐）';
  continueBtn.onclick = () => {
    window.location.href = activeGameUrl(active);
  };
  row.appendChild(continueBtn);

  const abandonBtn = document.createElement('button');
  abandonBtn.className = 'btn-mini ghost';
  abandonBtn.type = 'button';
  abandonBtn.textContent = '放弃旧牌局并新开';

  const errorLine = document.createElement('div');
  errorLine.className = 'p';
  let abandoning = false;
  abandonBtn.onclick = async () => {
    if (abandoning) return;
    abandoning = true;
    continueBtn.disabled = true;
    cancelBtn.disabled = true;
    abandonBtn.disabled = true;
    abandonBtn.textContent = '处理中...';
    errorLine.textContent = '';

    let remaining: ActiveGameInfo | null = null;
    try {
      const out = await abandonActiveGame(active.gameId);
      remaining = normalizeActiveGame((out as any)?.remainingActiveGame);
    } catch (err: unknown) {
      errorLine.textContent = `放弃失败：${String((err as any)?.message ?? err)}`;
      continueBtn.disabled = false;
      cancelBtn.disabled = false;
      abandonBtn.disabled = false;
      abandonBtn.textContent = '放弃旧牌局并新开';
      abandoning = false;
      return;
    }
    if (remaining) {
      errorLine.textContent = `放弃后仍有进行中的${modeLabelOfActiveGame(remaining)}（座位 ${remaining.seat + 1}）。`;
      continueBtn.disabled = false;
      cancelBtn.disabled = false;
      abandonBtn.disabled = false;
      abandonBtn.textContent = '放弃旧牌局并新开';
      abandoning = false;
      return;
    }

    closeModal();
    renderWelcomeStatus('已放弃旧牌局，正在继续新开流程…');
    if (onAbandonAndContinue) {
      try {
        await onAbandonAndContinue();
      } catch (err: unknown) {
        renderWelcomeStatus(`继续新开失败：${String((err as any)?.message ?? err)}`);
      }
    }
    abandoning = false;
  };
  row.appendChild(abandonBtn);

  const cancelBtn = document.createElement('button');
  cancelBtn.className = 'btn-mini ghost';
  cancelBtn.type = 'button';
  cancelBtn.textContent = '取消';
  cancelBtn.onclick = () => closeModal();
  row.appendChild(cancelBtn);

  body.appendChild(row);
  body.appendChild(errorLine);

  openModal('发现进行中的牌局', '已为你拦截新开局请求。', body);
}

async function ensureNoActiveGame(modeLabel: string, onAbandonAndContinue?: () => void | Promise<void>): Promise<boolean> {
  try {
    const { activeGame } = await fetchActiveGame();
    const active = normalizeActiveGame(activeGame);
    if (!active) return true;
    openActiveGameModal(active, modeLabel, onAbandonAndContinue);
    return false;
  } catch {
    // 网络/接口异常时不阻塞正常流程，仍交由后端最终校验
    return true;
  }
}

async function handleMultiTableFromServer(
  modeLabel: string,
  err: unknown,
  onAbandonAndContinue?: () => void | Promise<void>
): Promise<boolean> {
  const msg = String((err as any)?.message ?? err);
  const dataActive = normalizeActiveGame((err as any)?.data?.activeGame);
  if (!dataActive && !msg.includes(MULTI_TABLE_DENIED_KEYWORD)) {
    return false;
  }

  let active = dataActive;
  if (!active) {
    try {
      const out = await fetchActiveGame();
      active = normalizeActiveGame(out.activeGame);
    } catch {
      // ignore
    }
  }

  if (active) {
    openActiveGameModal(active, modeLabel, onAbandonAndContinue);
  } else {
    renderWelcomeStatus('你已有进行中的牌局，请先回到旧牌局。');
  }
  return true;
}

async function enterNewbieRoom(): Promise<void> {
  renderWelcomeStatus('正在进入新手房…');
  const { gameId } = await newbieStart();
  window.location.href = `hand/?gameId=${encodeURIComponent(gameId)}&room=newbie`;
}

async function enterGuobiaoNewbieRoom(): Promise<void> {
  renderWelcomeStatus('正在进入国际标准新手房…');
  const { gameId } = await guobiaoNewbieStart();
  window.location.href = `hand/?gameId=${encodeURIComponent(gameId)}&room=newbie&variant=guobiao`;
}

async function enterNewbieRoomByGameType(gameType: LandingGameType): Promise<void> {
  if (gameType === 'GUOBIAO') {
    await enterGuobiaoNewbieRoom();
    return;
  }
  await enterNewbieRoom();
}

async function ensureStartAllowed(modeLabel: string): Promise<boolean> {
  try {
    const { created, loginToken, apiToken, user } = await ensureUser();
    currentUser = user;
    currentMatchPoints = await fetchMatchPointsInfo();

    if (created) {
      openModal('已为你创建账号', '请先复制并保存登录口令，再开始对局。', buildTokenBody(loginToken, apiToken));
      if (currentMatchPoints.canClaimDaily) {
        queueDailyClaimModal(currentMatchPoints);
      }
      renderWelcomeStatus('账号已创建，请先备份登录口令。');
      return false;
    }

    renderWelcomeStatus();
    if (currentMatchPoints.matchPoints > 0) return true;

    const text = buildInsufficientText(currentMatchPoints);
    renderWelcomeStatus(text);
    openInsufficientRedirectModal(currentMatchPoints, modeLabel);
    return false;
  } catch (err: unknown) {
    renderWelcomeStatus(`校验失败：${String((err as any)?.message ?? err)}`);
    return false;
  }
}

async function onStartModeLink(link: HTMLAnchorElement, modeLabel: string): Promise<void> {
  const ok = await ensureStartAllowed(modeLabel);
  if (!ok) return;
  const noActive = await ensureNoActiveGame(modeLabel, () => {
    window.location.href = link.href;
  });
  if (!noActive) return;
  window.location.href = link.href;
}

function isAuthFailure(err: unknown): boolean {
  const msg = String((err as any)?.message ?? err).toLowerCase();
  return msg.startsWith('401') || msg.startsWith('403') || msg.includes('unauthorized') || msg.includes('forbidden');
}

async function bootstrapAuth(): Promise<void> {
  const existing = getLoginToken();
  if (!existing) {
    renderWelcomeStatus();
    return;
  }
  renderWelcomeStatus('正在同步账号状态…');
  try {
    const { user } = await fetchMeWithToken(existing);
    currentUser = user;
    currentMatchPoints = await fetchMatchPointsInfoWithToken(existing);
    renderWelcomeStatus();
    if (currentMatchPoints.canClaimDaily) {
      queueDailyClaimModal(currentMatchPoints);
    }
  } catch (err: unknown) {
    if (isAuthFailure(err)) {
      setLoginToken(null);
    }
    currentUser = null;
    currentMatchPoints = null;
    renderWelcomeStatus();
  }
}

function buildNewbieGameTypeBody(): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'field';

  const intro = document.createElement('div');
  intro.className = 'p';
  intro.textContent = '选择玩法后将直接进入对应新手房，并自动补齐 3 个 bot。';
  wrap.appendChild(intro);

  const row = document.createElement('div');
  row.className = 'row';
  const preferred = readPreferredGameType();
  for (const gameType of ['BLOOD_BATTLE', 'GUOBIAO'] as const) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = preferred === gameType ? 'btn-mini primary' : 'btn-mini ghost';
    btn.textContent = gameTypeLabel(gameType);
    styleChoiceButton(btn);
    btn.onclick = () => {
      writePreferredGameType(gameType);
      void startNewbieGame(gameType);
    };
    row.appendChild(btn);
  }
  wrap.appendChild(row);

  return wrap;
}

function onStartGame(): void {
  if (!btnStartGame) return;
  if (btnStartGame.disabled) return;
  openModal('选择玩法', '选择后直接进入对应新手房。', buildNewbieGameTypeBody());
}

async function startNewbieGame(gameType: LandingGameType): Promise<void> {
  if (!btnStartGame) return;
  if (btnStartGame.disabled) return;
  const prevStatus = statusEl.textContent ?? '';
  btnStartGame.disabled = true;
  closeModal();
  const label = newbieModeLabel(gameType);
  renderWelcomeStatus('正在校验对局积分…');
  try {
    const allowed = await ensureStartAllowed(label);
    if (!allowed) return;
    const noActive = await ensureNoActiveGame(label, async () => {
      try {
        await enterNewbieRoomByGameType(gameType);
      } catch (err: unknown) {
        if (await handleInsufficientFromServer(label, err)) return;
        renderWelcomeStatus(`进入失败：${String((err as any)?.message ?? err)}`);
      }
    });
    if (!noActive) return;

    await enterNewbieRoomByGameType(gameType);
    return;
  } catch (err: unknown) {
    if (await handleInsufficientFromServer(label, err)) {
      return;
    }
    if (
      await handleMultiTableFromServer(label, err, async () => {
        try {
          await enterNewbieRoomByGameType(gameType);
        } catch (innerErr: unknown) {
          if (await handleInsufficientFromServer(label, innerErr)) return;
          renderWelcomeStatus(`进入失败：${String((innerErr as any)?.message ?? innerErr)}`);
        }
      })
    ) {
      return;
    }
    renderWelcomeStatus(`进入失败：${String((err as any)?.message ?? err)}`);
  } finally {
    btnStartGame.disabled = false;
    if (
      statusEl.textContent === '正在校验对局积分…' ||
      statusEl.textContent === '正在进入新手房…' ||
      statusEl.textContent === '正在进入国际标准新手房…'
    ) {
      renderWelcomeStatus(prevStatus);
    }
  }
}

bootstrapAuth();
